import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ScrollToTop from '../components/ScrollToTop';
import API_BASE_URL from '../api';

const ReviewText = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const words = text.split(' ');
  const truncatedText = words.slice(0, 30).join(' ');

  return (
    <div className="mt-3 text-gray-800">
      <p className="text-sm sm:text-base leading-relaxed">{isExpanded ? text : truncatedText}</p>
      {words.length > 30 && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-500 hover:underline mt-2 text-sm"
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      )}
    </div>
  );
};

const TABS = ['All', 'Couple', 'Family', 'Business', 'Group', 'Solo'];

const Reviews = ({ embedded = false }) => {
  const [reviews, setReviews] = useState([]);
  const [lastTenRatings, setLastTenRatings] = useState([]);
  const [visibleReviews, setVisibleReviews] = useState(4);
  const [activeTab, setActiveTab] = useState('All');

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/reviews`);
        setReviews(response.data || []);
        const ratings = (response.data || []).map((review) => review.rating).slice(0, 10);
        setLastTenRatings(ratings);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };

    fetchReviews();
  }, []);

  const loadMoreReviews = () => {
    setVisibleReviews((prev) => prev + 4);
  };

  return (
    <article className="w-full">
      {!embedded && <ScrollToTop />}
      <section
        className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
          embedded ? 'py-6 lg:py-10' : 'pt-28 sm:pt-32 pb-12'
        }`}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
          User Rating & Reviews
        </h2>

        <div className="flex gap-3 sm:gap-4 text-sm sm:text-base mb-6 border-b border-gray-200 pb-2 overflow-x-auto">
          {TABS.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveTab(category)}
              className={`shrink-0 font-semibold pb-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === category
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-blue-500'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <aside className="w-full lg:w-80 xl:w-96 shrink-0 space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full shrink-0">
                <p className="text-xl sm:text-2xl font-bold text-blue-500">5.0</p>
              </div>
              <p className="text-blue-700 font-bold text-lg sm:text-xl">Excellent</p>
            </div>

            <table className="w-full text-sm sm:text-base">
              <tbody>
                {[
                  { label: 'Excellent', percentage: 95 },
                  { label: 'Very Good', percentage: 4 },
                  { label: 'Average', percentage: 1 },
                ].map((rating) => (
                  <tr key={rating.label} className="align-middle">
                    <td className="py-1.5 pr-2 text-right whitespace-nowrap w-[30%]">
                      {rating.label}
                    </td>
                    <td className="py-1.5 w-[45%]">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-2 bg-blue-500 rounded-full"
                          style={{ width: `${rating.percentage}%` }}
                        />
                      </div>
                    </td>
                    <td className="py-1.5 pl-2 w-[25%]">{rating.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div>
              <p className="font-bold text-sm sm:text-base">
                Last 10 Customer Ratings (Latest First)
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {lastTenRatings.length > 0 ? (
                  lastTenRatings.map((rating, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full text-blue-500 font-bold text-sm"
                    >
                      {rating}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No ratings yet</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-base sm:text-lg">Rating Categories</h3>
              <ul className="mt-3 space-y-2">
                {[
                  { category: 'Location', rating: 5.0 },
                  { category: 'Cleanliness', rating: 5.0 },
                  { category: 'Room', rating: 5.0 },
                  { category: 'Food', rating: 5.0 },
                  { category: 'Value for Money', rating: 5.0 },
                  { category: 'Hospitality', rating: 5.0 },
                ].map((item) => (
                  <li
                    key={item.category}
                    className="flex justify-between items-center text-sm sm:text-base gap-2"
                  >
                    <span>{item.category}</span>
                    <span className="bg-blue-100 text-blue-500 px-2 py-0.5 rounded-full text-sm font-semibold shrink-0">
                      {item.rating}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
              <h3 className="font-bold text-base sm:text-lg">Traveller Impressions</h3>
              <p className="text-gray-700 mt-2 text-sm sm:text-base leading-relaxed">
                The property is located near the airport and has clean and spacious rooms. Guests
                have appreciated the peaceful nature and beautiful views from the property. The food
                served at the restaurant is tasty and hygienic. The staff is polite, helpful, and
                professional. The check-in process is smooth and easy. However, some guests have
                complained about maintenance issues like non-functional AC, dirty bed sheets and
                towels, and lack of toiletries. Overall, the property is a good option for a
                comfortable stay near the airport.
              </p>
            </div>

            <div className="mt-8">
              <h3 className="font-bold text-base sm:text-lg mb-4">Customer Reviews</h3>
              {reviews.length === 0 ? (
                <p className="text-gray-500">No reviews yet.</p>
              ) : (
                reviews.slice(0, visibleReviews).map((review, index) => (
                  <div
                    key={review.id || index}
                    className="border-t border-gray-200 pt-4 mt-4 first:mt-0 first:border-t-0 first:pt-0"
                  >
                    <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                      <p className="font-bold text-gray-800">
                        {review.customerName || 'Anonymous'}
                      </p>
                      <span className="text-lg sm:text-xl font-bold text-gray-700 shrink-0">
                        {review.rating}/5
                      </span>
                    </div>
                    <ReviewText text={review.text} />
                    {review.images && review.images.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {review.images.map((image, imgIndex) => (
                          <img
                            key={imgIndex}
                            src={`${API_BASE_URL.replace('/v1/api', '')}/uploads/${image}`}
                            alt={`Review ${index + 1} - ${imgIndex + 1}`}
                            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded shadow"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}

              {visibleReviews < reviews.length && (
                <button
                  type="button"
                  onClick={loadMoreReviews}
                  className="mt-6 w-full sm:w-auto px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Load More Reviews
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default Reviews;
